import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react'

const CartContext = createContext(null)
const STORAGE_KEY = 'slimfast_cart'

function readCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const product = action.payload
      const qty = Math.max(1, Number(action.quantity) || 1)
      const existing = state.find((item) => item.id === product.id)
      if (existing) {
        return state.map((item) =>
          item.id === product.id
            ? { ...item, quantity: Math.min(99, item.quantity + qty) }
            : item,
        )
      }
      return [
        ...state,
        {
          id: product.id,
          name: product.name,
          slug: product.slug || '',
          sku: product.sku || '',
          price: Number(product.price) || 0,
          image: product.image || '',
          quantity: Math.min(99, qty),
        },
      ]
    }
    case 'SET_QTY': {
      const quantity = Math.max(1, Math.min(99, Number(action.quantity) || 1))
      return state.map((item) =>
        item.id === action.id ? { ...item, quantity } : item,
      )
    }
    case 'REMOVE':
      return state.filter((item) => item.id !== action.id)
    case 'CLEAR':
      return []
    default:
      return state
  }
}

export function CartProvider({ children }) {
  const [items, dispatch] = useReducer(cartReducer, [], readCart)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  }, [items])

  const value = useMemo(() => {
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0)
    const subtotal = items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    )

    return {
      items,
      itemCount,
      subtotal,
      addToCart: (product, quantity = 1) =>
        dispatch({ type: 'ADD', payload: product, quantity }),
      setQuantity: (id, quantity) =>
        dispatch({ type: 'SET_QTY', id, quantity }),
      removeFromCart: (id) => dispatch({ type: 'REMOVE', id }),
      clearCart: () => dispatch({ type: 'CLEAR' }),
    }
  }, [items])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) {
    throw new Error('useCart must be used within CartProvider')
  }
  return ctx
}
